import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { createManualLead } from '@/api/inspection-leads'
import { formatPhoneInput } from '@/utils/phone'

interface Props {
  onClose: () => void
}

type ManualResult = { status: string; leadId?: string; duplicate?: boolean; reason?: string }

interface FormState {
  name: string
  phone: string
  location: string
  price: string
}

const DEFAULTS: FormState = { name: '', phone: '', location: '', price: '4990' }

/**
 * Модалка «Создать лид вручную» — для физиков НЕ с Avito/лендинга (звонок,
 * walk-in). Шлёт в edge `manual-lead` (source='Manual'), цена опциональна.
 * Согласия ПДн/оферта клиент даст позже на лендинге.
 */
export function ManualLeadDialog({ onClose }: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [form, setForm] = useState<FormState>(DEFAULTS)
  const [result, setResult] = useState<ManualResult | null>(null)

  const mut = useMutation({
    mutationFn: async (): Promise<ManualResult> => {
      const name = form.name.trim()
      const phone = form.phone.trim()
      const location = form.location.trim()
      if (!name || !phone || !location) {
        return { status: 'error', reason: 'missing_fields' }
      }
      const priceRaw = form.price.trim()
      let price: number | undefined
      if (priceRaw !== '') {
        const n = Number(priceRaw)
        if (Number.isFinite(n) && n >= 0) price = Math.round(n)
      }
      return createManualLead({ name, phone, location, price })
    },
    onSuccess: (r) => {
      setResult(r)
      if (r.status === 'ok') {
        void qc.invalidateQueries({ queryKey: ['inspection-leads'] })
      }
    },
  })

  const pending = mut.isPending
  const isOk = result?.status === 'ok'

  return (
    <Modal
      title={t('leads.manual.title')}
      onClose={onClose}
      footer={
        isOk ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.done')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setResult(null)
                mut.mutate()
              }}
              disabled={pending}
            >
              {pending ? t('leads.manual.creating') : t('leads.manual.create')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.manual.desc')}
        </p>

        <Input
          label={t('leads.manual.clientName')}
          required
          value={form.name}
          onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          disabled={pending || isOk}
        />
        <Input
          label={t('leads.manual.phone')}
          required
          value={form.phone}
          onChange={(e) => setForm((s) => ({ ...s, phone: formatPhoneInput(s.phone, e.target.value) }))}
          disabled={pending || isOk}
          inputMode="tel"
          placeholder="+7 999 123 45 67"
        />
        <Input
          label={t('leads.manual.locationRegion')}
          required
          value={form.location}
          onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))}
          disabled={pending || isOk}
        />
        <Input
          label={t('leads.manual.priceLabel')}
          inputMode="numeric"
          value={form.price}
          onChange={(e) => setForm((s) => ({ ...s, price: e.target.value.replace(/[^\d]/g, '') }))}
          disabled={pending || isOk}
          hint={t('leads.manual.priceHint')}
        />

        {result ? (
          result.status === 'ok' ? (
            <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200">
              <div className="font-medium">
                {result.duplicate ? t('leads.manual.duplicate') : t('leads.manual.created')}
              </div>
              <div className="mt-1 break-all">
                ID: <code className="font-mono">{result.leadId}</code>
              </div>
              {result.leadId ? (
                <Link
                  to={`/inspection-leads/${result.leadId}`}
                  onClick={onClose}
                  className="mt-2 inline-block underline"
                >
                  {t('leads.manual.openCard')}
                </Link>
              ) : null}
            </div>
          ) : (
            <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
              {t('leads.manual.error', { reason: result.reason ?? 'unknown' })}
            </div>
          )
        ) : null}
      </div>
    </Modal>
  )
}
